import { APP_INITIALIZER, inject, Provider } from '@angular/core';

import { KeycloakService } from '../authentication/keycloak.service';
import { ShellApplication } from '../core';

export interface XhrInterceptorConfig {
  urlIncludes: string[];
}

const MIN_TOKEN_TTL_SECONDS = 30;

export function provideXHRInterceptor(config: XhrInterceptorConfig): Provider {
    return {
        provide: APP_INITIALIZER,
        useFactory: () => {
            const keycloakService = inject(KeycloakService);
            const shellApp = inject(ShellApplication);

            return () => initializeXHRInterceptor(config, keycloakService, shellApp);
        },
        multi: true
    };
}

function initializeXHRInterceptor(
    config: XhrInterceptorConfig,
    keycloakService: KeycloakService,
    shellApp: ShellApplication
): void {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(
        method: string,
        url: string,
        async: boolean = true
    ) {
        (this as any)._interceptedUrl = url;
        return originalOpen.call(this, method, url, async);
    };

    XMLHttpRequest.prototype.send = function(body: any) {
        const requestUrl = (this as any)._interceptedUrl ?? '';
        const shouldAttachToken = config.urlIncludes.some((pattern) => requestUrl.includes(pattern));

        if (shouldAttachToken) {
            attachAuthorizationHeader(this, keycloakService, shellApp).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('XHR Interceptor: Failed to set Authorization header', err);
            });
        }

        return originalSend.call(this, body);
    };
}

async function attachAuthorizationHeader(
    xhr: XMLHttpRequest,
    keycloakService: KeycloakService,
    shellApp: ShellApplication
): Promise<void> {
    const client = keycloakService.getClient() ?? shellApp.authClient;
    if (!client) {
        return;
    }

    if (client.isTokenExpired(MIN_TOKEN_TTL_SECONDS)) {
        await client.updateToken(MIN_TOKEN_TTL_SECONDS);
    }

    const token = client?.token;
    if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
}
